// src/components/dialog/DlgPanelSensors.tsx

import { ExpandMoreTwoTone } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Input,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { PricePool } from '@/types/domain'
import { FolderId, FolderPool } from '@/types/domain/folder'
import { SensorId, SensorPool } from '@/types/domain/sensor'
import { KARMA_WizardDialog } from '@/types/karma/dialog'

import { DashboardDisplaySensors, DlgCloseButton, HStack } from '..'
import {
  OperatorData,
  useFolderPool,
  useOperator,
  usePricePool,
  useSensorPool,
} from '../../hooks'
import useDashboardPagination from '../../hooks/tools/useDashboardPagination'

const energyTypes = [
  { label: 'Strom', value: 1 },
  { label: 'Wasser', value: 2 },
  { label: 'Gas', value: 3 },
]

const indicatorType = {
  consumption: 1,
  consumption_per_sqm: 2,
  cost: 3,
  cost_per_sqm: 4,
  co2: 5,
  co2_per_sqm: 6,
}

type DlgPanelSensorsProps = KARMA_WizardDialog & {
  data: {
    folders: FolderId[]
    sensors: SensorId[]
  }
  onChange: (sIds: SensorId[]) => void
}

export const DlgPanelSensors = ({
  data,
  onChange,
  onClose,
  onGotoNext,
  onGotoPrev,
  open,
}: DlgPanelSensorsProps) => {
  const { folders, sensors } = data

  const opData: OperatorData = useOperator()
  const fPool: FolderPool = useFolderPool()
  const sPool: SensorPool = useSensorPool()
  const pPool: PricePool = usePricePool()

  const panelShape = useMemo(() => {
    const y: number = new Date().getFullYear()
    const m: number = new Date().getMonth()
    const startDate: Date = new Date(y, m - 1, 1)
    const endDate: Date = new Date(y, m, 0)

    return {
      granularity: 'd',
      graphic_type: 1,
      indicator: 1,
      name: 'Neue Kachel',
      position: 0,
      presentation_level: null,
      startDate,
      endDate,
    }
  }, [])

  const [panelData, setPanelData] = useState(panelShape)

  const [energyChecked, setEnergyChecked] = useState<boolean>(false)
  const [, setWaterChecked] = useState<boolean>(false)
  const [, setGasChecked] = useState<boolean>(false)

  const [selectedType, setSelectType] = useState<number>(1)

  const [sensorsSearch, setSensorsSearch] = useState<string>('')

  const [showFolderSensors, setShowFolderSensors] = useState<boolean>(false)

  const [energyData, setEnergyData] = useState([])
  const [waterData, setWaterData] = useState([])
  const [gasData, setGasData] = useState([])

  const [count, setCount] = useState<number>(0)
  const [page, setPage] = useState<number>(1)

  const ENERGYDATA = useDashboardPagination(energyData)
  const WATERDATA = useDashboardPagination(waterData)
  const GASDATA = useDashboardPagination(gasData)

  // fn's

  const handlePageChange = (event, page) => {
    setPage(page)
    ENERGYDATA.jump(page)
    WATERDATA.jump(page)
    GASDATA.jump(page)
  }

  const handleToggleSensors = (event, type) => {
    const data =
      type === 'energy'
        ? energyData
        : type === 'water'
          ? waterData
          : type === 'gas'
            ? gasData
            : null

    const sIds: SensorId[] = []

    data.map(s => {
      const { entity_id } = s.attributes

      sIds.push(entity_id)

      return null
    })

    let updatedIds: SensorId[] = event.target.checked
      ? [...data, ...sIds]
      : data.filter(f => !sIds.includes(f))

    if (event.target.checked === false && updatedIds.length === 0)
      updatedIds = []

    onChange([...new Set(updatedIds)])
  }

  const onToggleAll = event => {
    const type: string =
      selectedType === 1 ? 'energy' : selectedType === 2 ? 'water' : 'gas'

    handleToggleSensors(event, type)
    setEnergyChecked(prev => !prev)
  }

  // useEffects

  useEffect(() => {
    if (open === false) return

    setPanelData(panelShape)
  }, [open, panelShape])

  useEffect(() => {
    if (sPool.length === 0 || fPool.length === 0) return

    const indicator: number = panelData?.indicator

    const FILTER_INDICATOR = s =>
      indicator === indicatorType.consumption
        ? true
        : indicator === indicatorType.consumption_per_sqm
          ? s.attributes.area_in_sqm !== null
          : indicator === indicatorType.cost
            ? pPool.find(f => f.sensor_id === s.attributes.entity_id)
            : indicator === indicatorType.cost_per_sqm
              ? pPool.find(f => f.sensor_id === s.attributes.entity_id) &&
                s.attributes.area_in_sqm !== null
              : indicator === indicatorType.co2
                ? s.attributes.co2_factor !== null
                : s.attributes.co2_factor !== null &&
                  s.attributes.area_in_sqm !== null

    const FILTER_FOLDERS = f => folders.includes(Number(f.attributes.folder_id))

    const FILTER_SEARCH = s => {
      const { name: sensorName, folder_id } = s.attributes
      const fRecord = fPool.find(f => Number(f.id) === Number(folder_id))
      const label = `${sensorName} | Gebäude ${fRecord ? fRecord.attributes.name : ''}`

      return label.toLowerCase().includes(sensorsSearch.toLowerCase())
    }

    const FILTER_BY_TYPE = type =>
      sPool
        .filter(
          f =>
            f.attributes.name !== null &&
            (f.attributes.type_in_folder === type ||
              f.attributes.type_in_folder === `${type}_detail`)
        )
        .filter(FILTER_INDICATOR)
        .filter(FILTER_SEARCH)
        .filter(FILTER_FOLDERS)

    const count = Math.ceil(
      FILTER_BY_TYPE(
        selectedType === 1 ? 'energy' : selectedType === 2 ? 'water' : 'gas'
      ).length / 10
    )
    setCount(count)

    setEnergyData(FILTER_BY_TYPE('energy'))
    setWaterData(FILTER_BY_TYPE('water'))
    setGasData(FILTER_BY_TYPE('gas'))
  }, [
    folders,
    fPool,
    panelData?.indicator,
    sPool,
    pPool,
    sensorsSearch,
    selectedType,
  ])

  const onEnergyTypeChange = event => {
    const energyType = event.target.value
    setSelectType(energyType)
  }

  useEffect(() => {
    if (open === false) {
      setEnergyChecked(false)
      setWaterChecked(false)
      setGasChecked(false)
    }
  }, [open])

  return (
    <Dialog fullWidth maxWidth={'xl'} open={open}>
      <DialogTitle>
        <Typography>Neue Kachel</Typography>

        <DlgCloseButton onClick={onClose} />
      </DialogTitle>

      <DialogContent>
        <TextField
          label={'Energiemedium'}
          onChange={onEnergyTypeChange}
          select
          value={selectedType || ''}
        >
          {energyTypes.map(item => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreTwoTone />}>
            <HStack sx={{ width: 1 }}>
              <Typography>Messpunkte</Typography>

              <Chip
                label={`${sensors.length}/${selectedType === 1 ? energyData.length : selectedType === 2 ? waterData.length : gasData.length}`}
              />
            </HStack>
          </AccordionSummary>

          <Divider />
          <Input
            onChange={event => setSensorsSearch(event.target.value)}
            placeholder={'Suchen ...'}
            sx={{ m: 0, minHeight: 23, pl: 1 }}
            value={sensorsSearch}
          />

          <AccordionDetails>
            {opData?.isAdmin && (
              <HStack sx={{ width: 1 }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showFolderSensors}
                        onClick={() => setShowFolderSensors(prev => !prev)}
                      />
                    }
                    label={'Nur zugeordnete Sensoren anzeigen (Admin Fn.)'}
                  />
                </FormGroup>
              </HStack>
            )}

            <HStack
              alignItems={'top'}
              justifyContent={'space-between'}
              sx={{ display: 'flex' }}
            >
              <FormGroup sx={{ width: 'calc(100% / 3)' }}>
                <FormControlLabel
                  label={
                    selectedType === 1
                      ? 'Strom'
                      : selectedType === 2
                        ? 'Wasser'
                        : 'Gas'
                  }
                  control={
                    <Checkbox checked={energyChecked} onChange={onToggleAll} />
                  }
                />

                <DashboardDisplaySensors
                  selectedFolders={folders}
                  selectedSensors={sensors}
                  data={
                    selectedType === 1
                      ? ENERGYDATA.currentData()
                      : selectedType === 2
                        ? WATERDATA.currentData()
                        : GASDATA.currentData()
                  }
                  presentationLevel={panelData?.presentation_level}
                  setSelectedSensors={onChange}
                />
              </FormGroup>
            </HStack>

            <Stack>
              <Pagination
                count={count}
                onChange={handlePageChange}
                page={page}
                shape={'rounded'}
                size={'large'}
                variant={'outlined'}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button onClick={onGotoPrev}>Zurück</Button>
        <Button onClick={onGotoNext}>Weiter</Button>
      </DialogActions>
    </Dialog>
  )
}
